// App updates and ratings (contracts/app-updates.md; backend/api src/schemas/paywall.ts AdminAppVersions and
// AdminRatings): GET /admin/v1/app/versions (installs per app version, devices updated in the last 30 days) and
// GET /admin/v1/ratings?from&to (rating prompts requested per ISO week by moment, and "Rate" taps). The settings
// are the config keys app.ios and rating.prompt (GET/PUT /config). Every role reads; the config is admin.

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { RatingPrompt } from "../lib/appUpdates";
import { request } from "./client";

const count = z.number().int().min(0);
const rate = z.number().min(0).max(1);

export const AppVersions = z.object({
  /** Installs whose device row was updated since then (30 days). */
  since: z.string(),
  latestVersion: z.string(),
  minimumVersion: z.string(),
  total: z.object({ devices: count, players: count }),
  /** `version: null` is an app before 1.1 (it reports none); share is of devices. */
  items: z.array(z.object({ version: z.string().nullable(), devices: count, players: count, share: rate })),
  onLatest: z.object({ devices: count, share: rate.nullable() }),
  belowMinimum: z.object({ devices: count, share: rate.nullable() }),
  /** The last App Store lookup of auto mode. */
  lastCheck: z.object({ checkedAt: z.string(), storeVersion: z.string().nullable(), outcome: z.string() }).nullable(),
});
export type AppVersions = z.infer<typeof AppVersions>;

export const RatingStats = z.object({
  from: z.string(),
  to: z.string(),
  config: RatingPrompt.catch(() => ({ enabled: true, minActiveDays: 0, minSessions: 0, cooldownDays: 0, perVersion: true, moments: [] })),
  /** ISO weeks (UTC), oldest first. */
  weeks: z.array(z.object({ week: z.string(), from: z.string(), to: z.string(), requested: count, byMoment: z.record(z.string(), count), rateTaps: count })),
  totals: z.object({ requested: count, byMoment: z.record(z.string(), count), rateTaps: count, players: count }),
});
export type RatingStats = z.infer<typeof RatingStats>;

export const appApi = {
  versions: (signal?: AbortSignal) => request(AppVersions, "GET", "/app/versions", { signal }),
  /** Default: the last 12 weeks (to − 83 days … today, UTC). */
  ratings: (range: { from?: string; to?: string } = {}, signal?: AbortSignal) => request(RatingStats, "GET", "/ratings", { query: range, signal }),
};

export const useAppVersions = (enabled = true) => useQuery({ queryKey: ["app", "versions"], queryFn: ({ signal }) => appApi.versions(signal), enabled });
export const useRatingStats = (enabled = true) => useQuery({ queryKey: ["app", "ratings"], queryFn: ({ signal }) => appApi.ratings({}, signal), enabled });
